import { useEffect, useState } from "react";
import { RgbColorPicker } from "react-colorful";
import { INode } from "../../@types/device";
import { ServiceContextType } from "../../@types/service";
import { rawValue, rgbKeys } from "../../lib/node";
import { useSetter } from "../../lib/useSetter";

/**
 * The old ColorPicker held colour in local state and never called update(), so
 * no tumbler ever changed. This writes each changed channel, debounced.
 */
export default function ColorWidget({
  node,
  ctx,
}: {
  node: INode;
  ctx: ServiceContextType;
}) {
  const { setValue, disabled } = useSetter();
  const keys = rgbKeys(node);

  const live = {
    r: rawValue(ctx, node.addr, keys.r) ?? 0,
    g: rawValue(ctx, node.addr, keys.g) ?? 0,
    b: rawValue(ctx, node.addr, keys.b) ?? 0,
  };

  // Local state drives the picker while dragging; the live value re-syncs it
  // whenever the hardware reports something different.
  const [color, setColor] = useState(live);
  useEffect(() => {
    setColor((c) =>
      c.r === live.r && c.g === live.g && c.b === live.b ? c : live
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.r, live.g, live.b]);

  const onChange = (next: { r: number; g: number; b: number }) => {
    setColor(next);
    if (disabled) return;
    // Only write the channels that actually moved.
    if (next.r !== live.r) setValue(node.addr, keys.r, Math.round(next.r), 200);
    if (next.g !== live.g) setValue(node.addr, keys.g, Math.round(next.g), 200);
    if (next.b !== live.b) setValue(node.addr, keys.b, Math.round(next.b), 200);
  };

  return (
    <div className="flex flex-col items-center py-3 gap-2">
      <RgbColorPicker color={color} onChange={onChange} />
      <div className="text-xs opacity-50">
        rgb({color.r}, {color.g}, {color.b})
      </div>
    </div>
  );
}
